import React, { useCallback, useContext, useEffect, useState } from "react";
import classnames from "classnames";

import { AppContext } from "@vahak/core/dist/app-context";
import { FONT_WEIGHTS } from "@vahak/core/dist/constants/misc";
import { usePlaceSearch, useStateSearch } from "@vahak/core/dist/hooks/useSearch";
// import { getHeightByViewPort,getWidthByViewPort } from "@vahak/core/dist/methods/getSizeByViewPort";
import { getRemValue } from "@vahak/core/dist/methods/getRemValue";
import SearchBlueIcon from "@vahak/core/dist/icons/SearchBlue.svg";
import CloseCircleIcon from "@vahak/core/dist/icons/CloseCircle.svg";
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { InputChangeEventHandler } from "@vahak/core/dist/custom-types/handler";
import QuickFilterPill from "./quick-filter-pill/QuickFilterPill";
import AppInput from "@vahak/core/dist/components/app-input/AppInput";
import Search from "@vahak/core/dist/components/search/Search";
import AppRadioBtn from "@vahak/core/dist/components/app-radio-btn/AppRadioBtn";

import Styles from "./DesktopSearchCard.module.scss";
import { useTopDest } from "@vahak/core/dist/hooks/useTopDest";
import CloseBtnStyles from "./CloseBtn.module.scss";
import { useTranslation } from "next-i18next";
import { AppRadioBtnPropsAlias } from "@vahak/core/dist/components/index";
import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";

const PopularDest = [
    {
        state: "Maharashtra",
        category: "suggested" as "suggested",
        categoryLabel: "POPULAR DESTINATIONS" as "POPULAR DESTINATIONS"
    },
    {
        state: "Rajasthan",
        category: "suggested" as "suggested",
        categoryLabel: "POPULAR DESTINATIONS" as "POPULAR DESTINATIONS"
    },
    {
        state: "Tamil Nadu",
        category: "suggested" as "suggested",
        categoryLabel: "POPULAR DESTINATIONS" as "POPULAR DESTINATIONS"
    }
];

export interface DesktopSearchCardProps {
    isSearchCardMinimized: boolean;
    searchBy?: "location" | "name";
    onChangeSearchBy: AppRadioBtnPropsAlias["onChange"];
    src: CitySearchResultWithCateg | undefined;
    onSelectSrc: (city: CitySearchResultWithCateg | undefined) => void;
    dest: CitySearchResultWithCateg | undefined;
    onSelectDest: (city: CitySearchResultWithCateg | undefined) => void;
    selectedDestQuickFilter: CitySearchResultWithCateg;
    nameSearchVal: string;
    onInputNameSearch: InputChangeEventHandler;
    onClearName?: () => void;
}

const DesktopSearchCard = ({
    isSearchCardMinimized,
    searchBy,
    onChangeSearchBy,
    src,
    onSelectSrc,
    dest,
    onSelectDest,
    selectedDestQuickFilter,
    nameSearchVal,
    onInputNameSearch,
    onClearName
}: DesktopSearchCardProps) => {
    const { t } = useTranslation(["transporters"]);
    const { currentLocation, recentTransporterCitySearches } = useContext(AppContext);

    const { topDests: _topDests, setTopDests } = useTopDest({ max: 4 });

    const [isDestinationSearchVisible, setDestinationSearchVisible] = useState(false);

    const [destSearchClear, setDestSearchClear] = useState(0);

    const {
        isSearchLoading: isSourceSearchLoading,
        setIsSearchLoading: setIsSourceSearchLoading,
        onSearchInput: onSourceSearchInput,
        searchOptions: sourceSearchOptions,
        setSearchOptions: setSrcSearchOptions,
        searchText: sourceSearchText,
        labelKey: sourceSearchLabelKey,
        onInputChange: onSourceSearchInputChange,
        onItemSelected: onSourceSearchItemSelected,
        // selectedItem: selectedSource,
        setSelectedItem: setSelectedSource
    } = usePlaceSearch({
        locationParamsOption: {
            source: LocationSearchSource.TransporterProfile
        },
        showCurrentLocationOption: true,
        defaultOptions: recentTransporterCitySearches,
        onSelectItem: (selectedItems: CitySearchResultWithCateg[]) => {
            onSelectSrc(selectedItems[0]);
        }
    });

    const {
        labelKey: destinationSearchLabelKey,
        isSearchLoading: isDestinationSearchLoading,
        onSearchInput: onDestinationSearchInput,
        searchOptions: destinationSearchOptions,
        searchText: destinationSearchText,
        onInputChange: onDestinationSearchInputChange,
        onItemSelected: onDestinationSearchItemSelected,
        selectedItem: destinationSearchSelectedItem,
        setSelectedItem: setSelectedDest
    } = useStateSearch({
        locationParamsOption: {
            source: LocationSearchSource.TransporterProfile
        },
        showCurrentLocationOption: true,
        defaultOptions: PopularDest,
        onSelectItem: (item: CitySearchResultWithCateg[]) => {
            setDestinationSearchVisible(false);
            if (item[0]) {
                const destState = { state: item[0].name, state_id: item[0].id };
                onSelectDest(destState);
                setTopDests(destState as any);
            }
        }
    });

    const onClickDestinationSearchBtn = useCallback(() => {
        setSelectedDest(undefined);
        setDestinationSearchVisible(true);
    }, []);

    const onBlurDestinationSearch = useCallback(() => {
        if (!destinationSearchSelectedItem) {
            setDestinationSearchVisible(false);
            setDestSearchClear((state) => state + 1);
            // setDestSearchInputVal("");
        }
    }, [destinationSearchSelectedItem]);

    const onClickDestQuickFilter = (selectedItem: CitySearchResultWithCateg) => {
        setSelectedDest(selectedItem);
        onSelectDest(selectedItem);
        setTopDests(selectedItem);
    };

    const onDeselectDestQuickFilter = () => {
        setSelectedDest(undefined);
        onSelectDest(undefined);
        // setTopDests(selectedItem);
    };

    const _onClearName = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();
        onClearName?.();
    };

    useEffect(() => {
        currentLocation?.lat && setSelectedSource({ name: String(currentLocation?.lat) });
        setIsSourceSearchLoading(false);
    }, [currentLocation]);

    useEffect(() => {
        recentTransporterCitySearches && setSrcSearchOptions(recentTransporterCitySearches);
    }, [recentTransporterCitySearches]);

    useEffect(() => {
        selectedDestQuickFilter.state_id && setTopDests(selectedDestQuickFilter);
    }, [selectedDestQuickFilter]);

    return (
        <div
            style={{
                height: isSearchCardMinimized ? "100px" : "150px",
                boxShadow: "0px 10px 20px #CDCDCD29",
                backgroundColor: "white",
                position: "relative",
                padding: isSearchCardMinimized ? `0 67px` : "0 30px",
                marginTop: !isSearchCardMinimized ? "-75px" : "unset"
            }}
        >
            <div className="search-by" style={{ position: "absolute", top: "20px" }} hidden={isSearchCardMinimized}>
                <span
                    style={{
                        // fontWeight: FONT_WEIGHTS.bold,
                        fontSize: getRemValue(20),
                        color: "#202124",
                        fontFamily: "Inter-Bold"
                    }}
                >
                    {t("header_filter_section.search_transporters_label")}
                </span>
                <AppRadioBtn
                    labelText={t("header_filter_section.search_by.location")}
                    name="serachBy"
                    value={"location"}
                    isChecked={searchBy === "location"}
                    style={{ marginLeft: "50.67px" }}
                    onChange={onChangeSearchBy}
                />
                <AppRadioBtn
                    labelText={t("header_filter_section.search_by.name")}
                    name="serachBy"
                    value="name"
                    isChecked={searchBy === "name"}
                    style={{ marginLeft: "43px" }}
                    onChange={onChangeSearchBy}
                />
            </div>
            <div
                className="search-controls"
                style={{
                    display: "flex",
                    position: "absolute",
                    width: isSearchCardMinimized ? `calc(100% - 2*16px)` : "calc(100% - 60px)",
                    bottom: isSearchCardMinimized ? "8px" : searchBy === "location" ? "10px" : "10px"
                }}
            >
                {searchBy === "location" && (
                    <div
                        className="source-search"
                        style={{
                            borderRight: "1px solid rgba(128, 134, 139, 0.2)",
                            width: isSearchCardMinimized ? "418.5px" : "390px",
                            paddingRight: "20px"
                        }}
                    >
                        <Search
                            id="transporter-source-search"
                            isLoading={isSourceSearchLoading}
                            onSearch={onSourceSearchInput}
                            onSelectItem={onSourceSearchItemSelected}
                            onInputChange={onSourceSearchInputChange}
                            selectedItem={src}
                            options={sourceSearchOptions}
                            searchText={sourceSearchText}
                            labelText={t("header_filter_section.source_city.title")}
                            labelKey={sourceSearchLabelKey}
                            labelClassName={Styles.srcSearchLabel}
                            activeClassName={Styles.activeSrc}
                            dirtyClassName={Styles.dirtySrc}
                            labelStyles={{
                                left: 0,
                                padding: 0,
                                color: "#202124",
                                fontSize: `${getRemValue(18)}`,
                                top: "4px",
                                // fontWeight: FONT_WEIGHTS.bold,
                                fontFamily: "var(--font-semiBold)",

                                lineHeight: "10px",
                                letterSpacing: 0
                            }}
                            // setFocus={1}
                            placeholder={t("header_filter_section.source_city.placeholder")}
                            containerStyles={{ height: "73px", border: "none" }}
                            inputWrapperStyles={{
                                padding: "0",
                                fontSize: getRemValue(22),
                                letterSpacing: getRemValue(-0.22),
                                fontWeight: FONT_WEIGHTS.normal,
                                marginTop: "12px"
                            }}
                            inputWrapperClassName={isSearchCardMinimized ? "srcSearch__Sticky" : "srcSearch"}
                            minLength={0}
                            showClearBtn={true}
                        />
                    </div>
                )}
                {searchBy === "location" && (
                    <div className="destination-search" style={{ paddingLeft: "20px" }}>
                        <Search
                            id="transporter-destination-search"
                            isLoading={isDestinationSearchLoading}
                            onSearch={onDestinationSearchInput}
                            onSelectItem={onDestinationSearchItemSelected}
                            selectedItem={destinationSearchSelectedItem}
                            onInputChange={onDestinationSearchInputChange}
                            options={destinationSearchOptions}
                            searchText={destinationSearchText}
                            labelKey={destinationSearchLabelKey}
                            labelText={t("header_filter_section.destination_state.title")}
                            labelStyles={{
                                left: 0,
                                padding: 0,
                                color: "#202124",
                                fontSize: `${getRemValue(18)}`,
                                top: "4px",
                                // fontWeight: FONT_WEIGHTS.bold,
                                fontFamily: "var(--font-semiBold)",
                                lineHeight: "10px",
                                letterSpacing: 0
                            }}
                            placeholder={t("header_filter_section.destination_state.placeholder")}
                            setFocus={isDestinationSearchVisible ? 1 : 0}
                            containerStyles={{ width: "280px", height: "73px", border: "none" }}
                            inputWrapperStyles={{
                                padding: "0",
                                fontSize: getRemValue(22),
                                letterSpacing: getRemValue(-0.22),
                                fontWeight: FONT_WEIGHTS.normal,
                                marginTop: "12px",
                                position: "absolute",
                                display: !isDestinationSearchVisible ? "none" : undefined,
                                // display: "none",
                                backgroundColor: "white"
                            }}
                            inputWrapperClassName={classnames(
                                isDestinationSearchVisible
                                    ? "destination-search_input-wrapper"
                                    : "destination-search_input-wrapper_disappear",
                                isSearchCardMinimized ? "destSearch__Sticky" : "destSearch"
                            )}
                            onBlur={onBlurDestinationSearch}
                            clear={destSearchClear}
                        />
                        <div
                            style={{
                                height: "42px",
                                lineHeight: "42px",
                                marginTop: "-50px",
                                position: "absolute",
                                display: "flex",
                                // display: !isDestinationSearchVisible ? "inline-flex" : "none",
                                alignItems: "center"
                                // width: "max-content"
                            }}
                            className={
                                !isDestinationSearchVisible
                                    ? "destination-shortcut-chips"
                                    : "destination-shortcut-chips_disappear"
                            }
                        >
                            {_topDests.slice(0, 4).map((state) => (
                                <QuickFilterPill
                                    key={state?.state_id}
                                    text={state?.state}
                                    isMobile={false}
                                    isSelected={state?.state_id === (dest?.state_id || dest?.id)}
                                    onSelected={onClickDestQuickFilter}
                                    onDeselected={onDeselectDestQuickFilter}
                                    className={Styles.dest_search_quick_filter}
                                    data={state}
                                    selectedClassName={Styles.selectedStateFilterPill}
                                />
                            ))}
                            <div
                                style={{
                                    borderRadius: "29px",
                                    backgroundColor: "rgba(12, 126, 255, 0.1)",
                                    border: "1px solid transparent",
                                    padding: `9.4px 20px`,
                                    fontSize: getRemValue(14),
                                    letterSpacing: getRemValue(-0.14),
                                    color: "#1053FF",
                                    lineHeight: "18px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    fontFamily: "var(--font-medium)"
                                }}
                                onClick={onClickDestinationSearchBtn}
                            >
                                <SearchBlueIcon style={{ marginRight: "9px" }} />
                                {t("header_filter_section.search_button_label")}
                            </div>
                        </div>
                    </div>
                )}

                {searchBy === "name" && (
                    <div className={Styles.nameSearch}>
                        <label
                            style={{
                                // left: 0,
                                // padding: 0,
                                color: "#202124",
                                fontSize: `${getRemValue(18)}`,
                                // top: "4px",
                                // fontWeight: FONT_WEIGHTS.bold,
                                fontFamily: "var(--font-semiBold)",
                                lineHeight: "10px",
                                letterSpacing: 0
                                // marginTop: "12px"
                            }}
                        >
                            {t("header_filter_section.search_by_name.title")}
                        </label>
                        <AppInput
                            isActiveByDefault
                            placeholder={t("header_filter_section.search_by_name.placeholder")}
                            value={nameSearchVal}
                            labelText=""
                            name="nameSearch"
                            onChange={onInputNameSearch}
                            type="text"
                            containerStyles={{
                                width: "100%",
                                border: "none"
                            }}
                            inputWrapperStyles={{ padding: 0, marginTop: "-9px" }}
                            inputStyles={{
                                fontSize: getRemValue(22),
                                letterSpacing: getRemValue(-0.22),
                                fontWeight: FONT_WEIGHTS.medium,
                                color: "#1053FF"
                            }}
                            autoComplete={false}
                            valueSuffix={
                                nameSearchVal?.trim() && (
                                    <span
                                        className={CloseBtnStyles.btn}
                                        style={{ marginLeft: "auto" }}
                                        onClick={_onClearName}
                                    >
                                        <CloseCircleIcon />
                                    </span>
                                )
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DesktopSearchCard;
